import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import { Refresh, MapPin } from "@/components/icons";
import { Button } from "@/components/ui/button";

import { fetchMapLocations, MapLocation } from "@/lib/api";
import { Stat } from "@/components/Stat";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";

/* ---- Fix Leaflet's broken default icons in Vite ---- */
// @ts-ignore
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

/* ---- Colored marker icons ---- */
function createColorIcon(color: string): L.Icon {
  const svgIcon = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 36" width="24" height="36">
      <path d="M12 0C5.4 0 0 5.4 0 12c0 9 12 24 12 24s12-15 12-24C24 5.4 18.6 0 12 0z" fill="${color}" stroke="#333" stroke-width="1"/>
      <circle cx="12" cy="12" r="5" fill="white"/>
    </svg>`;
  return L.icon({
    iconUrl: `data:image/svg+xml;base64,${btoa(svgIcon)}`,
    iconSize: [24, 36],
    iconAnchor: [12, 36],
    popupAnchor: [0, -36],
  });
}

const ICONS: Record<string, L.Icon> = {
  online: createColorIcon("#22c55e"),
  offline: createColorIcon("#ef4444"),
  mixed: createColorIcon("#f59e0b"),
  no_units: createColorIcon("#9ca3af"),
};

const STATUS_BADGE: Record<string, "success" | "error" | "warning" | "neutral"> = {
  online: "success",
  offline: "error",
  mixed: "warning",
  no_units: "neutral",
};

/* ---- Auto-fit bounds ---- */
function FitBounds({ locations }: { locations: MapLocation[] }) {
  const map = useMap();
  useEffect(() => {
    if (locations.length === 0) return;
    const bounds = L.latLngBounds(locations.map((l) => [l.lat, l.lon]));
    map.fitBounds(bounds, { padding: [30, 30] });
  }, [locations, map]);
  return null;
}

export function MapPage() {
  const [offset, setOffset] = useState(0);
  const [allLocations, setAllLocations] = useState<MapLocation[]>([]);
  const limit = 100;

  const q = useQuery({
    queryKey: ["map", "locations", offset],
    queryFn: () => fetchMapLocations(limit, offset),
    refetchInterval: 60_000,
  });

  useEffect(() => {
    if (q.data?.locations) {
      if (offset === 0) {
        setAllLocations(q.data.locations);
      } else {
        setAllLocations((prev) => {
          const existingIps = new Set(prev.map((l) => l.ip));
          const newLocs = q.data.locations.filter((l) => !existingIps.has(l.ip));
          return [...prev, ...newLocs];
        });
      }
    }
  }, [q.data, offset]);

  const totalLocations = q.data?.total_locations ?? 0;
  const hasMore = allLocations.length < totalLocations;

  return (
    <div className="space-y-5 pt-[86px]">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Map</h1>
          <div className="text-sm opacity-70">Geographic distribution of EVSE units</div>
        </div>
        <Button variant="ghost" size="sm" onClick={() => { setOffset(0); q.refetch(); }}>
          <Refresh className="h-4 w-4" /> Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="grid gap-3 md:grid-cols-4">
        <Stat title="Total Units" value={q.data?.total_units ?? 0} icon={<MapPin className="h-4 w-4 opacity-60" />} />
        <Stat title="Locations" value={q.data?.total_locations ?? 0} />
        <Stat title="Unique IPs" value={q.data?.total_unique_ips ?? 0} />
        <Stat title="Pending Resolution" value={q.data?.pending_ips ?? 0} />
      </div>

      {q.isLoading && allLocations.length === 0 ? (
        <div className="flex justify-center p-12">
          <span className="animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent" />
        </div>
      ) : q.isError ? (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">Failed to load map locations</div>
      ) : (
        <>
          {/* Map */}
          <Card className="overflow-hidden">
            <div style={{ height: "500px" }}>
              <MapContainer
                center={[39.8283, -98.5795]}
                zoom={4}
                style={{ height: "100%", width: "100%" }}
                scrollWheelZoom={true}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/">OSM</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <FitBounds locations={allLocations.filter((l) => l.lat && l.lon)} />

                {allLocations.filter((l) => l.lat && l.lon).map((loc) => (
                  <Marker
                    key={loc.ip}
                    position={[loc.lat, loc.lon]}
                    icon={ICONS[loc.status] || ICONS.no_units}
                  >
                    <Popup>
                      <div className="min-w-[200px]">
                        <div className="font-semibold">{loc.city}, {loc.country}</div>
                        <div className="text-xs opacity-60">{loc.state}</div>
                        <div className="text-xs font-mono mt-1">{loc.ip}</div>
                        <div className="text-xs mt-1">ISP: {loc.isp || "Unknown"}</div>
                        <div className="mt-2 flex gap-2 text-xs">
                          <span className="text-success">{loc.online_count} online</span>
                          <span className="text-error">{loc.offline_count} offline</span>
                          <span>({loc.unit_count} total)</span>
                        </div>
                        {loc.units?.length > 0 && (
                          <div className="mt-2 space-y-1">
                            {loc.units.slice(0, 5).map((u) => (
                              <div key={u.unit_id} className="flex justify-between text-xs">
                                <span className="font-mono">{u.name || u.unit_id}</span>
                                <span className={u.is_online ? "text-success" : "text-error"}>
                                  {u.is_online ? u.state_name || "online" : "offline"}
                                </span>
                              </div>
                            ))}
                            {loc.units.length > 5 && (
                              <div className="text-xs opacity-50">+{loc.units.length - 5} more</div>
                            )}
                          </div>
                        )}
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </div>
          </Card>

          {/* Legend */}
          <div className="flex flex-wrap items-center gap-4 text-sm">
            <span className="font-medium">Legend:</span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 rounded-full bg-success"></span> All Online
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 rounded-full bg-warning"></span> Mixed
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 rounded-full bg-error"></span> All Offline
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 rounded-full bg-neutral/40"></span> No Units
            </span>
          </div>

          {/* Load More */}
          {hasMore && (
            <div className="flex justify-center">
              <Button
                variant="outline"
                disabled={q.isFetching}
                onClick={() => setOffset((prev) => prev + limit)}
              >
                {q.isFetching ? "Loading..." : `Load More (${allLocations.length}/${totalLocations})`}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
